import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  PRESETS,
  applyLook,
  blendGrade,
  stampPreset,
  applyGradeOps,
  activeAdjustments,
  ADJUSTMENT_SCHEMA,
  baseGrade as computeNeutral,
  setPath,
} from '../utils/grade';
import firebase from '../firebaseConfig';
import GradedCanvas from './GradedCanvas';
import { loadImageEl, diffReport, targetedMoves, applyTargeted } from '../utils/colorMatch';

const API = process.env.REACT_APP_UPLOAD_API || '';
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Build the grade a proposal describes: optionally lay a preset down (blended by
// amount, carrying the current decode), then apply the slider ops + masks on top.
function applyProposal(grade, ops, preset, masks, clearMasks) {
  let next = grade;
  if (preset && preset.id) {
    const p = PRESETS.find((x) => x.id === preset.id);
    if (p) {
      const look = applyLook(next, p.grade);
      const amt = (preset.amount == null ? 100 : preset.amount) / 100;
      next = blendGrade(next, look, amt);
      next = stampPreset(next, p, preset.amount == null ? 100 : preset.amount);
    }
  }
  next = applyGradeOps(next, ops || []);
  if (clearMasks || (Array.isArray(masks) && masks.length)) {
    const existing = clearMasks ? [] : (next.masks || []);
    next = { ...next, masks: [...existing, ...(masks || [])] };
  }
  return next;
}

// AI color/light assistant. Two ways to drive it:
//   • Variations — "give me 3 looks", preview them live on the current frame,
//     click one to apply, optionally add direction and regenerate (iterate).
//   • Chat — describe a change in words; it tweaks the sliders/masks directly.
// Either way the model SEES the master vs current graded frame (captureFrames).
const ColorAssistant = forwardRef(function ColorAssistant({ grade, baseGrade, source, getCrop, onApply, onReframe, captureFrames, reference, onSetReference, onAutoAlign }, ref) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('variations'); // 'variations' | 'chat' | 'match'

  // Match-to-target state
  const [matchBusy, setMatchBusy] = useState(false);
  const [alignFirst, setAlignFirst] = useState(true);
  const [aligning, setAligning] = useState(false);
  const [pendingAuto, setPendingAuto] = useState(false); // kick off the loop once preview is ready
  const runMatchRef = useRef(null);
  const [dE, setDE] = useState(null);
  const [matchLog, setMatchLog] = useState([]);
  const stopRef = useRef(false);

  // Chat state
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const undoRef = useRef(null);
  const scrollRef = useRef(null);

  // Variations state
  const [vars, setVars] = useState(null); // [{ label, reply, grade }]
  const [varsBusy, setVarsBusy] = useState(false);
  const [dir, setDir] = useState('');
  const [pickedIdx, setPickedIdx] = useState(-1);

  const [err, setErr] = useState('');
  const gradeRef = useRef(grade);
  gradeRef.current = grade;
  const rootRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy]);

  const configured = !!API;
  const canPreview = !!source;

  // Shared request context (schema, active adjustments, presets, frames, token).
  const buildContext = useCallback(async () => {
    const frames = await captureFrames();
    const g = gradeRef.current;
    const activeMap = Object.fromEntries(activeAdjustments(g).map((a) => [a.key, a.value]));
    const schema = ADJUSTMENT_SCHEMA.map((s) => ({
      key: s.key, label: s.label, min: s.min, max: s.max, step: s.step, unit: s.unit,
      default: activeMap[s.key] == null ? s.default : activeMap[s.key],
    }));
    const user = firebase.auth().currentUser;
    const token = user ? await user.getIdToken() : null;
    return {
      token,
      body: {
        schema,
        activeAdjustments: activeAdjustments(g).map((a) => ({ key: a.key, value: a.value })),
        presets: PRESETS.filter((p) => p.id !== 'none').map((p) => ({ id: p.id, name: p.name })),
        currentPreset: g._preset ? { id: g._preset.id, amount: g._preset.amount } : null,
        images: frames,
      },
    };
  }, [captureFrames]);

  // ---- Variations ---------------------------------------------------------
  const generate = async (overrideDir) => {
    if (varsBusy) return;
    const message = (typeof overrideDir === 'string' ? overrideDir : dir).trim();
    setErr('');
    setVarsBusy(true);
    setPickedIdx(-1);
    try {
      const { token, body } = await buildContext();
      const res = await fetch(`${API}/ai/color/variations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ...body, n: 3, message }),
      });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
        throw new Error(detail || `request failed (${res.status})`);
      }
      const data = await res.json();
      const list = Array.isArray(data.variations) ? data.variations : [];
      if (!list.length) throw new Error('no variations returned');
      const base = gradeRef.current;
      setVars(list.map((v) => ({
        label: v.label,
        reply: v.reply,
        grade: applyProposal(base, v.ops, v.preset, v.masks, v.clearMasks),
      })));
    } catch (e) {
      setErr(e.message || 'Could not generate variations');
    } finally {
      setVarsBusy(false);
    }
  };

  const pick = (i) => {
    if (!vars || !vars[i]) return;
    undoRef.current = gradeRef.current;
    onApply(vars[i].grade);
    setPickedIdx(i);
  };

  // ---- Match to a target reference ----------------------------------------
  // Measure how far the current graded frame is from the reference (perceptual
  // ΔE2000) and return the diff report + the captured frames used.
  const measure = useCallback(async () => {
    if (!reference?.dataUrl) return null;
    const frames = await captureFrames();
    const curF = frames.find((f) => /current graded/i.test(f.label)) || frames[frames.length - 1];
    if (!curF) return null;
    const [curEl, tgtEl] = await Promise.all([loadImageEl(curF.dataUrl), loadImageEl(reference.dataUrl)]);
    const rep = diffReport(curEl, tgtEl);
    setDE(rep.dE);
    return { rep, frames };
  }, [reference, captureFrames]);

  const onPickReference = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => { onSetReference?.({ dataUrl: String(r.result), name: file.name }); setDE(null); setMatchLog([]); };
    r.readAsDataURL(file);
  };

  // When a reference loads (e.g. via the header button), show a baseline ΔE
  // right away so the user sees how far off they are before running a match.
  useEffect(() => {
    if (!reference?.dataUrl || !canPreview) return undefined;
    const t = setTimeout(() => { measure().catch(() => {}); }, 450);
    return () => clearTimeout(t);
  }, [reference?.dataUrl, canPreview, measure]);

  // Iterate: measure → ask Grok for a step toward the target → apply → repeat.
  const doAutoAlign = async () => {
    if (!onAutoAlign || aligning) return;
    setAligning(true);
    try {
      const ok = await onAutoAlign();
      await sleep(450); // let the cropped preview re-render
      await measure();
      if (ok === false) setMatchLog((l) => [{ round: 0, dE: null, reply: 'Auto-align: could not find a confident match — nudge the framing manually.' }, ...l].slice(0, 12));
    } catch { /* noop */ } finally {
      setAligning(false);
    }
  };

  // Guarded hill-climb: each round we ask for a step, APPLY it, re-measure, and
  // keep it ONLY if ΔE dropped — otherwise revert and tell the model its last
  // move was reverted. ΔE is therefore monotonically non-increasing: the loop
  // can never make the match worse than where it started.
  const runMatch = async (rounds) => {
    if (!reference?.dataUrl || matchBusy) return;
    setErr(''); setMatchBusy(true); stopRef.current = false;
    const log = (entry) => setMatchLog((l) => [entry, ...l].slice(0, 16));
    try {
      if (alignFirst && onAutoAlign && rounds > 1) {
        await onAutoAlign();
        await sleep(500);
      }
      const base = await measure();
      if (!base) throw new Error('could not capture the current frame');
      let bestDE = base.rep.dE;
      let bestRep = base.rep;
      let bestGrade = gradeRef.current;
      undoRef.current = bestGrade; // a single Undo reverts the whole match
      log({ round: 0, dE: bestDE, reply: `Baseline ΔE ${bestDE.toFixed(1)} — converging…` });
      let lastReverted = false;

      // Render a candidate grade and measure its ΔE (apply → settle → measure).
      const evalGrade = async (g) => { onApply(g); await sleep(440); const m = await measure(); return m ? m.rep : null; };

      for (let i = 0; i < rounds; i += 1) {
        if (stopRef.current) break;
        if (bestDE < 2.0) { log({ round: i + 1, dE: bestDE, reply: 'Converged — ΔE under 2.' }); break; }
        const { token, body } = await buildContext();
        const images = [...body.images, { label: 'TARGET (reproduce this look exactly)', dataUrl: reference.dataUrl }];
        const hint = lastReverted
          ? 'IMPORTANT: your last change was reverted (no improvement). Make a SMALLER, DIFFERENT move; use precise per-band colorGrading (teal h≈190 into shadows/midtones, warm h≈50 into highlights) and HSL aqua/blue saturation, not just global.'
          : '';
        const note = [dir.trim(), hint].filter(Boolean).join(' ');
        const res = await fetch(`${API}/ai/color/match`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ ...body, images, report: bestRep.text, message: note }),
        });
        if (!res.ok) {
          let detail = '';
          try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
          throw new Error(detail || `request failed (${res.status})`);
        }
        const data = await res.json();
        const ops = Array.isArray(data.ops) ? data.ops : [];
        const preset = data.preset || null;
        const masks = Array.isArray(data.masks) ? data.masks : [];
        const clearMasks = data.clearMasks === true;

        // Candidate set: the model's proposal (preset line-searched over blend
        // amounts) + deterministic band-guided targeted moves → line-search by ΔE.
        const cands = [];
        if (preset && preset.id) {
          const baseAmt = preset.amount == null ? 100 : preset.amount;
          const amts = Array.from(new Set([baseAmt, 40, 70, 90].map((a) => Math.max(5, Math.min(100, a)))));
          for (const a of amts) cands.push({ tag: `${preset.id}@${a}`, grade: applyProposal(bestGrade, ops, { id: preset.id, amount: a }, masks, clearMasks) });
          cands.push({ tag: 'ops-only', grade: applyProposal(bestGrade, ops, null, masks, clearMasks) });
        } else if (ops.length || masks.length || clearMasks) {
          cands.push({ tag: 'model', grade: applyProposal(bestGrade, ops, null, masks, clearMasks) });
        }
        for (const mv of targetedMoves(bestRep.metrics).slice(0, 16)) cands.push({ tag: mv.tag, grade: applyTargeted(bestGrade, mv, setPath) });
        if (!cands.length) { log({ round: i + 1, dE: bestDE, reply: data.reply || 'No change proposed.' }); lastReverted = false; continue; }

        let pick = null;
        for (const c of cands) {
          if (stopRef.current) break;
          const rep = await evalGrade(c.grade);
          if (rep && (!pick || rep.dE < pick.rep.dE)) pick = { c, rep };
        }
        if (pick && pick.rep.dE < bestDE - 0.05) {
          const delta = bestDE - pick.rep.dE;
          bestDE = pick.rep.dE; bestRep = pick.rep; bestGrade = pick.c.grade;
          onApply(bestGrade); await sleep(300); await measure();
          lastReverted = false;
          log({ round: i + 1, dE: bestDE, reply: `✓ −${delta.toFixed(1)} ΔE · [${pick.c.tag}] ${data.reply || ''}` });
        } else {
          onApply(bestGrade); await sleep(300); await measure();
          lastReverted = true;
          log({ round: i + 1, dE: bestDE, reply: `✗ no improvement (best ${pick ? pick.rep.dE.toFixed(1) : '—'}) · ${data.reply || ''}` });
        }
      }
      onApply(bestGrade); // make sure the best grade is what's left on screen
      await sleep(250);
      await measure();
    } catch (e) {
      setErr(e.message || 'match failed');
    } finally {
      setMatchBusy(false);
    }
  };
  runMatchRef.current = runMatch;

  // When opened with autoRun, fire the full align + match loop as soon as the
  // preview is ready (so an upload runs hands-free, no clicks).
  useEffect(() => {
    if (!pendingAuto) return undefined;
    if (!reference?.dataUrl || !canPreview || !configured) return undefined;
    const t = setTimeout(() => {
      setPendingAuto(false);
      runMatchRef.current?.(8);
    }, 650);
    return () => clearTimeout(t);
  }, [pendingAuto, reference?.dataUrl, canPreview, configured]);

  // Imperative entry points for the editor header / voice copilot.
  useImperativeHandle(ref, () => ({
    runVariations: (direction) => {
      setOpen(true);
      setTab('variations');
      if (typeof direction === 'string') setDir(direction);
      generate(direction);
    },
    // Jump straight into the match-target flow (called by the header button).
    // opts.autoRun starts the align + match loop automatically.
    openMatch: (opts) => {
      setOpen(true);
      setTab('match');
      if (opts?.autoRun) setPendingAuto(true);
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    },
  }));

  // ---- Chat ---------------------------------------------------------------
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setErr('');
    const history = msgs.map((m) => ({ role: m.role, text: m.text }));
    setMsgs((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const { token, body } = await buildContext();
      const res = await fetch(`${API}/ai/color`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ...body, message: text, history }),
      });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
        throw new Error(detail || `request failed (${res.status})`);
      }
      const data = await res.json();
      const ops = Array.isArray(data.ops) ? data.ops : [];
      const preset = data.preset || null;
      const masks = Array.isArray(data.masks) ? data.masks : [];
      const clearMasks = data.clearMasks === true;
      const framing = data.framing && data.framing.found ? data.framing : null;
      const hasChange = ops.length > 0 || !!preset || masks.length > 0 || clearMasks;
      if (hasChange) {
        undoRef.current = gradeRef.current;
        onApply(applyProposal(gradeRef.current, ops, preset, masks, clearMasks));
      }
      // Reframe/zoom is geometry, not grade — hand it to the editor's crop owner.
      let reframed = false;
      if (framing && onReframe) reframed = !!onReframe(framing);
      setMsgs((m) => [...m, { role: 'assistant', text: data.reply || 'Done.', ops, preset, masks, undo: hasChange, reframed }]);
    } catch (e) {
      setErr(e.message || 'Assistant failed');
      setMsgs((m) => [...m, { role: 'assistant', text: `⚠️ ${e.message || 'Assistant failed'}`, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const undoLast = () => {
    if (!undoRef.current) return;
    onApply(undoRef.current);
    undoRef.current = null;
    setMsgs((m) => m.map((x) => (x.undo ? { ...x, undo: false, undone: true } : x)));
    setPickedIdx(-1);
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const onDirKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); generate(); } };
  const presetName = (id) => (PRESETS.find((p) => p.id === id) || {}).name || id;

  const neutral = computeNeutral(grade);

  const tile = (g, label, sub, opts = {}) => (
    <div className={`cs-aiasst-tile${opts.picked ? ' is-picked' : ''}${opts.action ? ' is-action' : ''}`}>
      <div
        className="cs-aiasst-tile-canvas"
        onClick={opts.onClick}
        role={opts.onClick ? 'button' : undefined}
        title={opts.onClick ? 'Use this look' : undefined}
      >
        {canPreview ? (
          <GradedCanvas
            source={source}
            grade={g}
            baseGrade={baseGrade}
            getCrop={getCrop}
            maxWidth={360}
            className="cs-aiasst-tile-gl"
          />
        ) : (
          <div className="cs-aiasst-tile-ph">no preview</div>
        )}
        {opts.picked && <span className="cs-aiasst-tile-badge">✓ applied</span>}
      </div>
      <div className="cs-aiasst-tile-meta">
        <strong>{label}</strong>
        {sub ? <span>{sub}</span> : null}
      </div>
    </div>
  );

  return (
    <div className={`cs-aiasst${open ? ' is-open' : ''}`} ref={rootRef}>
      <button type="button" className="cs-aiasst-head" onClick={() => setOpen((o) => !o)}>
        <span className="cs-aiasst-title">✨ Color assistant</span>
        <span className="cs-aiasst-sub">{open ? 'Hide' : 'Generate looks · describe a change'}</span>
        <span className="cs-aiasst-chev">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="cs-aiasst-body">
          {!configured && <div className="cs-aiasst-warn">Assistant API isn’t configured in this build.</div>}

          <div className="cs-aiasst-tabs">
            <button type="button" className={`cs-aiasst-tab${tab === 'variations' ? ' on' : ''}`} onClick={() => setTab('variations')}>Variations</button>
            <button type="button" className={`cs-aiasst-tab${tab === 'match' ? ' on' : ''}`} onClick={() => setTab('match')}>Match target</button>
            <button type="button" className={`cs-aiasst-tab${tab === 'chat' ? ' on' : ''}`} onClick={() => setTab('chat')}>Chat</button>
          </div>

          {tab === 'variations' ? (
            <div className="cs-aiasst-vars">
              <div className="cs-aiasst-varctl">
                <input
                  type="text"
                  value={dir}
                  placeholder="Optional direction… e.g. “warmer, filmic, protect skin”"
                  onChange={(e) => setDir(e.target.value)}
                  onKeyDown={onDirKey}
                  disabled={varsBusy || !configured}
                />
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={generate} disabled={varsBusy || !configured}>
                  {varsBusy ? <><span className="cs-spinner" /> Generating…</> : (vars ? '↻ 3 more' : '✨ Generate 3')}
                </button>
              </div>
              <div className="cs-aiasst-tilegrid">
                {tile(neutral, 'Master', 'ungraded')}
                {tile(grade, 'Current', pickedIdx === -1 ? 'in progress' : 'updated')}
              </div>
              {vars && (
                <>
                  <div className="cs-aiasst-varslabel">Pick a look to apply it — then refine the direction and generate again.</div>
                  <div className="cs-aiasst-tilegrid">
                    {vars.map((v, i) => tile(v.grade, v.label, v.reply, {
                      onClick: () => pick(i),
                      picked: pickedIdx === i,
                      action: true,
                    }))}
                  </div>
                </>
              )}
              {pickedIdx >= 0 && (
                <div className="cs-aiasst-varslabel">
                  Applied “{vars[pickedIdx]?.label}”. Tweak it in the panel below, generate 3 more from here, or
                  <button type="button" className="cs-aiasst-undo" onClick={undoLast} style={{ marginLeft: 6 }}>Undo</button>
                </div>
              )}
              {!vars && !varsBusy && (
                <div className="cs-aiasst-empty">
                  I look at the master vs your current grade and propose three distinct, tasteful looks. Pick one to
                  apply, add a direction, and generate again to iterate — or finalize and keep tweaking by hand.
                </div>
              )}
              {err && <div className="cs-aiasst-err">{err}</div>}
            </div>
          ) : tab === 'match' ? (
            <div className="cs-aiasst-match">
              {!reference ? (
                <div className="cs-aiasst-empty">
                  Load a reference image (the look you want to reproduce). I’ll measure the perceptual
                  difference (ΔE) between your current grade and the target, then iteratively adjust the
                  grade to converge on it. Align the framing first (crop) so the comparison is fair.
                  <div style={{ marginTop: 10 }}>
                    <label className="cs-btn cs-btn-primary cs-btn-sm" style={{ cursor: 'pointer' }}>
                      Load reference…
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onPickReference(e.target.files?.[0])} />
                    </label>
                  </div>
                </div>
              ) : (
                <>
                  <div className="cs-aiasst-matchref">
                    <img src={reference.dataUrl} alt="target reference" className="cs-aiasst-refthumb" />
                    <div className="cs-aiasst-matchmeta">
                      <div className="cs-aiasst-refname" title={reference.name}>🎯 {reference.name || 'reference'}</div>
                      <div className={`cs-aiasst-de${dE != null && dE < 5 ? ' good' : ''}`}>
                        {dE == null ? 'ΔE — not measured' : `ΔE2000 = ${dE.toFixed(1)}`}
                        <span className="cs-help" style={{ marginLeft: 6 }}>{dE == null ? '' : dE < 2 ? 'identical' : dE < 5 ? 'very close' : dE < 10 ? 'close' : 'off'}</span>
                      </div>
                      <label className="cs-aiasst-relink">
                        Replace<input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onPickReference(e.target.files?.[0])} />
                      </label>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={dir}
                    placeholder="Optional note for the matcher… e.g. “ignore the sky, match the water”"
                    onChange={(e) => setDir(e.target.value)}
                    disabled={matchBusy || !configured}
                  />
                  {onAutoAlign && (
                    <div className="cs-aiasst-alignrow">
                      <button
                        type="button"
                        className="cs-btn cs-btn-ghost cs-btn-sm"
                        onClick={doAutoAlign}
                        disabled={matchBusy || aligning || !canPreview}
                        title="Structurally match this frame's crop to the reference framing (grade-invariant) so ΔE compares like-for-like"
                      >
                        {aligning ? <><span className="cs-spinner" /> Aligning…</> : '✨ Auto-align framing'}
                      </button>
                      <label className="cs-aiasst-alignchk" title="Auto-align the framing once before the auto-match loop">
                        <input type="checkbox" checked={alignFirst} onChange={(e) => setAlignFirst(e.target.checked)} disabled={matchBusy} />
                        Align before auto-match
                      </label>
                    </div>
                  )}
                  <div className="cs-aiasst-matchbtns">
                    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => measure()} disabled={matchBusy || aligning || !canPreview}>Measure ΔE</button>
                    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => runMatch(1)} disabled={matchBusy || !configured || !canPreview}>
                      {matchBusy ? <><span className="cs-spinner" /> …</> : '1 step'}
                    </button>
                    <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => runMatch(8)} disabled={matchBusy || !configured || !canPreview}>
                      {matchBusy ? <><span className="cs-spinner" /> Matching…</> : '✨ Auto-match (8)'}
                    </button>
                    {matchBusy && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { stopRef.current = true; }}>Stop</button>}
                    {undoRef.current && !matchBusy && <button type="button" className="cs-aiasst-undo" onClick={undoLast}>Undo</button>}
                  </div>
                  {matchLog.length > 0 && (
                    <div className="cs-aiasst-matchlog">
                      {matchLog.map((m, i) => (
                        <div key={i} className="cs-aiasst-matchrow">
                          <span className="cs-aiasst-matchround">#{m.round}</span>
                          <span className="cs-aiasst-matchde">ΔE {m.dE == null ? '—' : m.dE.toFixed(1)}</span>
                          <span className="cs-aiasst-matchreply">{m.reply}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {err && <div className="cs-aiasst-err">{err}</div>}
                </>
              )}
            </div>
          ) : (
            <>
              <div className="cs-aiasst-log" ref={scrollRef}>
                {msgs.length === 0 && (
                  <div className="cs-aiasst-empty">
                    Tell me what you want — e.g. <i>“warmer, lift the shadows a touch, tame the bright sky”</i> or
                    <i> “filmic teal-orange at about 60%.”</i> I look at the original vs the current graded frame and
                    adjust the sliders (and local masks) for you.
                  </div>
                )}
                {msgs.map((m, i) => (
                  <div key={i} className={`cs-aiasst-msg cs-aiasst-${m.role}${m.error ? ' is-err' : ''}`}>
                    <div className="cs-aiasst-bubble">{m.text}</div>
                    {m.role === 'assistant' && (m.ops?.length > 0 || m.preset || m.masks?.length > 0 || m.reframed) && (
                      <div className="cs-aiasst-ops">
                        {m.preset && <span className="cs-aiasst-op cs-aiasst-op-preset">look: {presetName(m.preset.id)} · {m.preset.amount ?? 100}%</span>}
                        {(m.ops || []).map((o, k) => (
                          <span key={k} className="cs-aiasst-op">{o.label || o.key}: {o.value}</span>
                        ))}
                        {(m.masks || []).map((mk, k) => (
                          <span key={`mk${k}`} className="cs-aiasst-op cs-aiasst-op-preset">+ {mk.type === 'linear' ? 'gradient' : 'radial'} mask</span>
                        ))}
                        {m.reframed && <span className="cs-aiasst-op cs-aiasst-op-preset">⤢ reframed</span>}
                        {m.undo && <button type="button" className="cs-aiasst-undo" onClick={undoLast}>Undo</button>}
                        {m.undone && <span className="cs-aiasst-undone">undone</span>}
                      </div>
                    )}
                  </div>
                ))}
                {busy && (
                  <div className="cs-aiasst-msg cs-aiasst-assistant">
                    <div className="cs-aiasst-bubble"><span className="cs-spinner" /> Looking at the frames…</div>
                  </div>
                )}
              </div>
              {err && <div className="cs-aiasst-err">{err}</div>}
              <div className="cs-aiasst-input">
                <textarea
                  rows={2}
                  value={input}
                  placeholder="Describe the change… (Enter to send)"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  disabled={busy || !configured}
                />
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={send} disabled={busy || !configured || !input.trim()}>
                  Send
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default ColorAssistant;
